import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { KnownSettingsNodeDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useSettingsTreeReducerContext } from '../reducer/settings-tree-reducer.context';
import { SettingsTreeDataRequestType } from '../reducer/settings-tree-reducer.types';
import { Snackbar } from '../../../ui/components/pure/snackbar/snackbar.component';
import { buildSettingsDeepLinkSearch, parseSettingsDeepLink } from './settings-deep-link';

/**
 * Keeps the Settings URL and the selected tree node in sync, in both directions:
 *
 *  - READ (URL -> selection): a deep-link in the URL is enqueued for selection. The reducer's
 *    reactive watcher performs the actual selection once the (lazily-loaded) node appears, then
 *    auto-expands its ancestors. If the tree finishes loading and the node never appears we stay on
 *    the root and show a generic toast.
 *  - WRITE (selection -> URL): selecting any node mirrors it back into the URL (via `replace`, so
 *    tree navigation doesn't pollute history) so the URL is always a shareable snapshot.
 *
 * Rendered inside `SettingsTreeReducerProvider`. Renders nothing except the fallback toast.
 */
export function SettingsTreeUrlSync() {
  const { state, dispatch, deepLinkUnresolved } = useSettingsTreeReducerContext();
  const history = useHistory();
  const location = useLocation();
  const [notFound, setNotFound] = useState<{ label?: string } | null>(null);

  const selectedNode = state.selectedNode;
  const target = useMemo(() => parseSettingsDeepLink(location.search), [location.search]);

  // The root project is selected by default and represents the "no specific node" state.
  const rootProjectId = useMemo(() => {
    const root = state.projectTree.nodes.find((n) => n.type === KnownSettingsNodeDtoType.Root);
    return state.projectTree.nodes.find((n) => n.parentId === root?.id)?.id;
  }, [state.projectTree.nodes]);

  const selectedMatchesTarget =
    !!target &&
    !!selectedNode &&
    selectedNode.entityId === target.entityId &&
    (target.scope === undefined || selectedNode.request.rootId === target.scope);

  // True while we are still resolving a node referenced by the URL. While set, the WRITE effect must
  // not overwrite the URL from the (default root) selection, or it would wipe the deep-link mid-resolve.
  const resolvingRef = useRef(false);
  // The last `location.search` we acted on, so the READ effect only reacts to genuine URL changes
  // (external navigation / shared link) and not to our own selection-driven writes.
  const handledSearchRef = useRef<string | null>(null);

  // READ (URL -> selection): act once per distinct URL.
  useEffect(() => {
    if (handledSearchRef.current === location.search) {
      return;
    }
    handledSearchRef.current = location.search;
    setNotFound(null);
    if (!target || selectedMatchesTarget) {
      resolvingRef.current = false;
      return;
    }
    resolvingRef.current = true;
    dispatch({
      type: 'ENQUEUE_NODE_SELECTION',
      payload: {
        entityId: target.entityId,
        rootId: target.scope,
        requestType: target.nodeType ?? SettingsTreeDataRequestType.ProjectChildren,
        // Landing on a node via a deep-link should also expand it (if it has children).
        expandNode: true
      }
    });
  }, [dispatch, location.search, target, selectedMatchesTarget]);

  // Stop "resolving" as soon as the target node is selected.
  useEffect(() => {
    if (selectedMatchesTarget) {
      resolvingRef.current = false;
    }
  }, [selectedMatchesTarget]);

  // FALLBACK: the tree finished loading but the node never appeared -> stay on root and warn.
  // `deepLinkUnresolved` is derived from the merged tree, so it can't fire before selection had its chance.
  useEffect(() => {
    if (!target || !deepLinkUnresolved) {
      return;
    }
    resolvingRef.current = false;
    setNotFound({ label: target.label });
    dispatch({ type: 'CLEAR_ENQUEUED_NODE_SELECTION' });
  }, [dispatch, target, deepLinkUnresolved]);

  // WRITE (selection -> URL): keep the URL a shareable snapshot of the selection.
  useEffect(() => {
    if (!selectedNode || resolvingRef.current) {
      return;
    }
    const nextSearch =
      selectedNode.id === rootProjectId
        ? ''
        : buildSettingsDeepLinkSearch({
            entityId: selectedNode.entityId,
            scope: selectedNode.request.rootId,
            nodeType: selectedNode.request.type,
            label: selectedNode.displayLabel
          });
    if (nextSearch !== location.search) {
      // Record what we're about to write so the READ effect doesn't treat it as an external change.
      handledSearchRef.current = nextSearch;
      history.replace({ search: nextSearch });
    }
  }, [history, location.search, rootProjectId, selectedNode]);

  if (notFound === null) {
    return null;
  }
  return (
    <Snackbar severity="warning" onClose={() => setNotFound(null)}>
      {notFound.label
        ? `Couldn't find "${notFound.label}" in this project's settings.`
        : 'The requested settings item could not be found.'}
    </Snackbar>
  );
}
